import type {
  AccountRange,
  AccountId,
  GroupId,
  MappedAccountsOfGroup,
  GroupMappedAccounts,
  GroupHierarchyEntry,
} from '../shared-types'
import type { GroupListState } from './types'

import { defineStore } from 'pinia'

import { useCommon } from './common'
import { hasCollapsedParent } from './utils/hierarchy'

export const useGroupList = defineStore('accountMappingGroupList', {
  state: (): GroupListState => ({
    focusedDropdown: '',
  }),
  actions: {
    persistDropdown(accountId: AccountId) {
      this.focusedDropdown = accountId
    },
    remap({
      accountId,
      prevAccountRange,
      accountRange,
    }: {
      accountId: AccountId
      prevAccountRange: AccountRange
      accountRange: AccountRange
    }) {
      const commonStore = useCommon()

      const accountRangePerGroup = commonStore.getAccountRangePerGroup

      commonStore.commitRemoveAccountFromGroup({
        accountId,
        uuid: accountRangePerGroup[prevAccountRange],
      })

      commonStore.commitAddAccountToGroup({
        accountIds: [accountId],
        uuid: accountRangePerGroup[accountRange],
      })

      commonStore.commitMarkAccountAsRemappedByUser(accountId)
    },
    toggleGroup(groupId: GroupId) {
      const commonStore = useCommon()

      if (commonStore.$state.groupCollapse?.[groupId]) {
        commonStore.expandGroup(groupId)
      } else {
        commonStore.collapseGroup(groupId)
      }
    },
    unmap({
      accountId,
      accountRange,
    }: {
      accountId: AccountId
      accountRange: AccountRange
    }) {
      const commonStore = useCommon()

      const accountRangePerGroup = commonStore.getAccountRangePerGroup

      commonStore.commitRemoveAccountFromGroup({
        accountId,
        uuid: accountRangePerGroup[accountRange],
      })

      commonStore.commitMarkAccountAsRemappedByUser(accountId)
    },
  },
  getters: {
    getFilteredAccountGroupIds(): Array<GroupId> {
      const commonStore = useCommon()

      const groupList = commonStore.getGroupListPerDisplay
      const groupCollapse = commonStore.$state.groupCollapse
      const groupHierarchy = commonStore.$state.groupHierarchy

      // Do not render anything that belongs under a collapsed ancestor
      return groupList.filter(
        (groupId) =>
          !hasCollapsedParent({
            groupCollapse,
            groupHierarchy,
            groupId,
          })
      )
    },
    getFilteredGroupMappedAccounts(): GroupMappedAccounts {
      const commonStore = useCommon()

      return commonStore.$state.groupMappedAccounts
    },
    getGroupsIdsByLevelSortDesc(): Array<
      GroupHierarchyEntry & { id: GroupId }
    > {
      const commonStore = useCommon()

      const groupHierarchy = commonStore.$state.groupHierarchy

      const hierarchyEntries = Object.keys(groupHierarchy).map(
        (hierarchyKey) => ({
          id: hierarchyKey,
          ...groupHierarchy[hierarchyKey],
        })
      )

      return hierarchyEntries.sort((a, b) => {
        if (a.level === b.level) {
          return 0
        }

        if (a.level > b.level) {
          return -1
        }

        return 1
      })
    },
    getExternallyMappedGroupIds(state): Array<GroupId> {
      const commonStore = useCommon()
      const externalNumbersByAccountRange =
        commonStore.getExternalNumbersByAccountRange

      const groupList = commonStore.getGroupListPerDisplay
      const groups = commonStore.$state.groups

      const key = Object.keys(externalNumbersByAccountRange).find(
        (externalNumber) => {
          return externalNumbersByAccountRange[externalNumber].includes(
            state?.focusedDropdown
          )
        }
      )

      const result = groupList.find((groupId) => {
        const group = groups[groupId]
        if (!group) {
          return false
        }

        return group.accountRange === key
      })

      return result ? [result] : []
    },

    getMappedAccountsNumbersPerGroup(): Record<GroupId, number> {
      const sortedHierarchy = this.getGroupsIdsByLevelSortDesc
      const mapping = this.getFilteredGroupMappedAccounts

      // As we are reducing an already sorted array, by descending level,
      // we can be sure parents can already access their children information
      return sortedHierarchy.reduce((acc, hierarchyItem) => {
        if (hierarchyItem.children.length === 0) {
          const mappingItem = mapping[hierarchyItem.id]
          acc[hierarchyItem.id] = mappingItem
            ? Object.keys(mappingItem).length
            : 0
        } else {
          const childrenMappedAccountNumber = hierarchyItem.children.reduce(
            (sum, child) => {
              const childNum = acc[child] ? acc[child] : 0
              return sum + childNum
            },
            0
          )

          acc[hierarchyItem.id] = childrenMappedAccountNumber
        }

        return acc
      }, {} as Record<GroupId, number>)
    },
    getUnsavedMappedAccounts() {
      const commonStore = useCommon()

      const unsavedHierarchy = commonStore.getUnsavedGroupsAndAncestors
      const groupMappedAccounts = commonStore.$state.groupMappedAccounts

      return Object.keys(groupMappedAccounts).reduce((acc, groupKey) => {
        if (unsavedHierarchy[groupKey]) {
          acc[groupKey] = Object.keys(groupMappedAccounts[groupKey]).reduce(
            (alteredAccounts, accountKey) => {
              if (!groupMappedAccounts[groupKey][accountKey].isSaved) {
                alteredAccounts[accountKey] = {
                  ...groupMappedAccounts[groupKey][accountKey],
                }
              }

              return alteredAccounts
            },
            {} as MappedAccountsOfGroup
          )
        }

        return acc
      }, {} as GroupMappedAccounts)
    },
  },
})
